/**
 * ChatMessage — Rich message rendering for the Data Agent chat.
 *
 * Supports 6 visual variants:
 *   - user: right-aligned blue bubble with white text
 *   - agent: left-aligned grey bubble with markdown rendering
 *   - system: centered info bar
 *   - correction: full-width card with info-blue left border
 *   - warning: full-width card with amber left border + option buttons
 *   - tool_result: compact inline bar for deterministic operation outputs
 *
 * Inline tables are extracted from JSON code blocks and rendered via InlineResultTable.
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
// @ts-ignore — remark-gfm v4 is ESM-only; types resolve at runtime
import remarkGfm from 'remark-gfm';
// @ts-ignore — rehype-raw is ESM-only
import rehypeRaw from 'rehype-raw';
import {
  Box,
  Chip,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Info as InfoIcon,
  AutoFixHigh as FixIcon,
  WarningAmber as WarningIcon,
  Reply as ReplyIcon,
  ErrorOutline as ErrorIcon,
  CheckCircle as CompleteIcon,
  OpenInNew as EditorIcon,
  Visibility as ReviewIcon,
  Transform as SqlIcon,
  Code as PythonIcon,
  CompareArrows as MatchIcon,
} from '@mui/icons-material';
import Button from '@mui/material/Button';
import InlineResultTable from './InlineResultTable';
import MessageWidgets from './MessageWidgets';
import type { WorkspaceMessage } from '../../services/workspaceApi';

// ── Types ────────────────────────────────────────────────────────────

export type MessageVariant = 'user' | 'agent' | 'system' | 'correction' | 'warning' | 'mapping_complete' | 'tool_result';

export interface ChatMessageProps {
  message: WorkspaceMessage;
  /** Called when the user clicks a warning option button. */
  onOptionClick?: (optionIndex: number, messageId: string) => void;
  /** Called when the user clicks a suggested next-step action. */
  onSuggestionClick?: (prompt: string, label?: string) => void;
  /** Whether this is the most recent agent message (enables suggestion interaction). */
  isLatestAgentMessage?: boolean;
  /** Called when the user clicks dismiss on a failed message. */
  onDismissFailure?: () => void;
  /** Called when user clicks "Open Mapping Editor" in a mapping_complete CTA. */
  onOpenEditor?: () => void;
  /** Called when user clicks a tool_result bar to focus the corresponding canvas node. */
  onFocusNode?: (operationId: string) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────

/** Determine the visual variant for a message based on role + metadata. */
export function getMessageVariant(msg: WorkspaceMessage): MessageVariant {
  if (msg.role === 'user') return 'user';
  if (msg.role === 'system') return 'system';
  if (msg.metadata?.type === 'correction') return 'correction';
  if (msg.metadata?.type === 'warning') return 'warning';
  if (msg.metadata?.type === 'mapping_complete') return 'mapping_complete';
  if (msg.metadata?.synthetic === true) return 'tool_result';
  return 'agent';
}

/** Try to extract a tabular result from assistant content (JSON code block). */
function tryParseTable(content: string): { columns: string[]; rows: any[][] } | null {
  const match = content.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1]);
    if (parsed.columns && Array.isArray(parsed.rows)) {
      return { columns: parsed.columns, rows: parsed.rows };
    }
  } catch {
    // not valid JSON table
  }
  return null;
}

/** Remove the JSON code block from content so it's not rendered twice. */
function stripTableBlock(content: string): string {
  return content.replace(/```(?:json)?\s*\{[\s\S]*?\}\s*```/, '').trim();
}

/** Extract [Re: ...] context prefix from a user message, returning the label and cleaned text. */
function extractNodeContext(content: string): { label: string | null; text: string } {
  const match = content.match(/^\[Re:\s*(.+?)\]\s*/);
  if (match) {
    return { label: match[1], text: content.slice(match[0].length) };
  }
  return { label: null, text: content };
}

// ── Tool result parsing ─────────────────────────────────────────────

interface ToolResultFields {
  operationType: string;
  outputName: string;
  rowCount: string;
  colCount: string | null;
  inputRefs: string;
}

const TOOL_RESULT_RE = /^Executed your (.+?):\s*output `(.+?)` \(([\d,]+) rows(?:,\s*(\d+) columns?)?\) from `(.*?)`\.$/;

/** Parse deterministic operation output content into structured fields. */
function parseToolResult(content: string, metadata?: Record<string, any>): ToolResultFields | null {
  // Prefer structured metadata when available (new messages)
  if (metadata?.output_name && metadata?.row_count != null) {
    const typeLabel = metadata.operation_type ?? 'transform';
    return {
      operationType: typeLabel,
      outputName: metadata.output_name,
      rowCount: typeof metadata.row_count === 'number' ? metadata.row_count.toLocaleString() : String(metadata.row_count),
      colCount: metadata.col_count != null ? String(metadata.col_count) : null,
      inputRefs: Array.isArray(metadata.input_refs) ? metadata.input_refs.join(', ') : String(metadata.input_refs ?? ''),
    };
  }
  // Fallback: parse the content string (legacy messages)
  const match = content.match(TOOL_RESULT_RE);
  if (!match) return null;
  const typeLabel = match[1].replace(/\s+/g, '_');
  return {
    operationType: typeLabel,
    outputName: match[2],
    rowCount: match[3],
    colCount: match[4] ?? null,
    inputRefs: match[5],
  };
}

const OP_TYPE_ICONS: Record<string, React.ReactElement> = {
  sql_transform: <SqlIcon sx={{ fontSize: 14 }} />,
  python_transform: <PythonIcon sx={{ fontSize: 14 }} />,
  fuzzy_match: <MatchIcon sx={{ fontSize: 14 }} />,
};

const OP_TYPE_COLORS: Record<string, string> = {
  sql_transform: '#0891b2',
  python_transform: '#7c3aed',
  fuzzy_match: '#d97706',
};

// ── Timestamp formatting ────────────────────────────────────────────

/** Format a created_at ISO string into a short time label and full date-time tooltip. */
function formatTimestamp(createdAt?: string): { short: string; full: string } | null {
  if (!createdAt) return null;
  try {
    const date = new Date(createdAt);
    if (isNaN(date.getTime())) return null;
    return {
      short: date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
      full: date.toLocaleString([], {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      }),
    };
  } catch {
    return null;
  }
}

// ── Markdown styles for agent messages ─────────────────────────────

const markdownSx = {
  '& p': { m: 0, mb: 0.75, '&:last-child': { mb: 0 } },
  '& ul, & ol': { m: 0, mb: 0.75, pl: 2.5 },
  '& li': { mb: 0.25, fontSize: 13, lineHeight: 1.5 },
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    mt: 1, mb: 0.5, fontWeight: 600, fontSize: 14, lineHeight: 1.4,
  },
  '& blockquote': {
    borderLeft: '3px solid',
    borderColor: 'divider',
    pl: 1,
    ml: 0,
    fontStyle: 'italic',
    color: 'text.secondary',
  },
  '& code': {
    fontFamily: '"Fira Code", "Roboto Mono", monospace',
    backgroundColor: 'rgba(0,0,0,0.06)',
    px: 0.5,
    py: 0.125,
    borderRadius: 0.5,
    fontSize: '0.8em',
  },
  '& pre': {
    m: 0,
    mb: 0.75,
    p: 1.5,
    borderRadius: 1,
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    overflowX: 'auto',
    fontSize: 12,
    lineHeight: 1.6,
    '& code': {
      backgroundColor: 'transparent',
      color: '#e2e8f0',
      p: 0,
      fontFamily: '"Fira Code", "Roboto Mono", monospace',
      fontSize: 12,
    },
  },
  '& table': {
    borderCollapse: 'collapse',
    mb: 0.75,
    fontSize: 12,
    '& th, & td': {
      border: '1px solid',
      borderColor: 'divider',
      px: 1,
      py: 0.5,
    },
    '& th': {
      bgcolor: 'grey.100',
      fontWeight: 600,
    },
  },
  '& strong': { fontWeight: 600 },
  '& a': { color: 'primary.main', textDecoration: 'none' },
};

// ── Components ───────────────────────────────────────────────────────

/** System message — centered info bar. */
const SystemMessageBar: React.FC<{ text: string }> = ({ text }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      py: 0.5,
    }}
  >
    <InfoIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
    <Typography variant="caption" color="text.disabled">
      {text}
    </Typography>
  </Box>
);

/** Correction message — full-width info-blue card. */
const CorrectionMessage: React.FC<{ message: WorkspaceMessage }> = ({ message }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        p: 1.5,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.info.main, 0.06),
        borderLeft: `3px solid ${theme.palette.info.main}`,
      }}
    >
      <FixIcon sx={{ fontSize: 16, color: 'info.main', mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
        >
          Review & Correction Agent
        </Typography>
        <Box sx={{ mt: 0.5, fontSize: 13, lineHeight: 1.5, ...markdownSx }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{message.content || ''}</ReactMarkdown>
        </Box>
      </Box>
    </Box>
  );
};

/** Warning message — full-width amber card with option buttons. */
const WarningMessage: React.FC<{
  message: WorkspaceMessage;
  onOptionClick?: (optionIndex: number, messageId: string) => void;
}> = ({ message, onOptionClick }) => {
  const theme = useTheme();
  const options: string[] = message.metadata?.options || [];

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        p: 1.5,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.warning.main, 0.06),
        borderLeft: `3px solid ${theme.palette.warning.main}`,
      }}
    >
      <WarningIcon sx={{ fontSize: 16, color: 'warning.main', mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          fontWeight={600}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
        >
          Review & Correction Agent
        </Typography>
        <Box sx={{ mt: 0.5, fontSize: 13, lineHeight: 1.5, ...markdownSx }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{message.content || ''}</ReactMarkdown>
        </Box>
        {options.length > 0 && (
          <Stack spacing={0.75} sx={{ mt: 1.5 }}>
            {options.map((opt, i) => (
              <Box
                key={i}
                onClick={() => onOptionClick?.(i, message.message_id)}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 500,
                  transition: 'background-color 0.15s',
                  '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.08) },
                }}
              >
                {opt}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

/** User or agent chat bubble with markdown support. */
const ChatBubble: React.FC<{
  message: WorkspaceMessage;
  onDismissFailure?: () => void;
  onSuggestionClick?: (prompt: string, label?: string) => void;
  isLatestAgentMessage?: boolean;
}> = ({ message, onDismissFailure, onSuggestionClick, isLatestAgentMessage }) => {
  const theme = useTheme();
  const isUser = message.role === 'user';
  const isFailed = message.metadata?.failed === true;
  // For user messages, prefer the friendly display_text stored in metadata
  // over the raw instruction sent to the agent.
  const content = (isUser && message.metadata?.display_text as string) || message.content || '';

  // Strip [Re: ...] prefix from user messages and show as context chip
  const { label: nodeContext, text: userText } = isUser ? extractNodeContext(content) : { label: null, text: content };
  const cleanContent = isUser ? userText : content;

  // Extract inline tables from agent messages
  const tableData = !isUser ? tryParseTable(cleanContent) : null;
  const displayContent = tableData ? stripTableBlock(cleanContent) : cleanContent;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
      {/* Node context chip — shown above user bubbles that reference a specific step */}
      {isUser && nodeContext && (
        <Chip
          icon={<ReplyIcon sx={{ fontSize: 11 }} />}
          label={nodeContext}
          size="small"
          variant="outlined"
          sx={{
            mb: 0.5,
            height: 20,
            fontSize: 10,
            fontWeight: 500,
            color: 'text.secondary',
            borderColor: alpha(theme.palette.primary.main, 0.3),
            '& .MuiChip-icon': { color: alpha(theme.palette.primary.main, 0.5) },
          }}
        />
      )}

      <Box
        sx={{
          maxWidth: '90%',
          px: 1.75,
          py: 1.25,
          borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          // Agent bubble: soft blue tint (Tailwind blue-50/100 range) so it
          // reads as "Migratr's voice" while staying low-contrast.
          bgcolor: isUser ? (isFailed ? alpha(theme.palette.error.main, 0.85) : 'primary.main') : '#f5f9ff',
          color: isUser ? '#fff' : 'text.primary',
        }}
      >
        {!isUser && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: 10,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'text.secondary',
              mb: 0.5,
            }}
          >
            Agent
          </Typography>
        )}

        {isUser ? (
          /* User: plain text, white on blue */
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: 13,
              lineHeight: 1.5,
              color: '#fff',
            }}
          >
            {displayContent}
          </Typography>
        ) : (
          /* Agent: markdown-rendered */
          <Box sx={{ fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word', ...markdownSx }}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{displayContent}</ReactMarkdown>
          </Box>
        )}

        {tableData && (
          <Box sx={{ mt: 1 }}>
            <InlineResultTable columns={tableData.columns} rows={tableData.rows} />
          </Box>
        )}

        {/* Structured widget blocks (e.g. suggested next-step actions) */}
        {!isUser && message.metadata?.widgets?.length > 0 && (
          <MessageWidgets
            widgets={message.metadata.widgets}
            onSuggestionClick={onSuggestionClick}
            disabled={!isLatestAgentMessage}
          />
        )}
      </Box>

      {/* Failed indicator */}
      {isFailed && (
        <Box
          onClick={onDismissFailure}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            mt: 0.25,
            cursor: onDismissFailure ? 'pointer' : 'default',
            '&:hover': onDismissFailure ? { opacity: 0.7 } : {},
          }}
        >
          <ErrorIcon sx={{ fontSize: 12, color: 'error.main' }} />
          <Typography variant="caption" color="error.main" sx={{ fontSize: 10 }}>
            Failed to send — click to dismiss
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/** Tool result bar — compact inline bar for deterministic operation outputs. */
const ToolResultBar: React.FC<{ message: WorkspaceMessage; onFocusNode?: (operationId: string) => void }> = ({ message, onFocusNode }) => {
  const theme = useTheme();
  const parsed = parseToolResult(message.content || '', message.metadata);
  const accentColor = parsed ? (OP_TYPE_COLORS[parsed.operationType] ?? theme.palette.success.main) : theme.palette.success.main;
  const opIcon = parsed ? (OP_TYPE_ICONS[parsed.operationType] ?? null) : null;
  const operationId = message.metadata?.operation_id as string | undefined;
  const isClickable = !!(operationId && onFocusNode);

  return (
    <Box
      onClick={isClickable ? () => onFocusNode(operationId) : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 0.75,
        px: 1.5,
        py: 0.75,
        borderRadius: 1.5,
        bgcolor: alpha(theme.palette.success.main, 0.06),
        border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
        ...(isClickable && {
          cursor: 'pointer',
          transition: 'background-color 0.15s, border-color 0.15s',
          '&:hover': {
            bgcolor: alpha(theme.palette.success.main, 0.12),
            borderColor: alpha(theme.palette.success.main, 0.45),
          },
        }),
      }}
    >
      <CompleteIcon sx={{ fontSize: 15, color: 'success.main' }} />
      {parsed ? (
        <>
          {opIcon && (
            <Box sx={{ display: 'flex', alignItems: 'center', color: accentColor }}>
              {opIcon}
            </Box>
          )}
          <Typography
            component="span"
            sx={{
              fontFamily: '"Fira Code", "Roboto Mono", monospace',
              fontSize: 12,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {parsed.outputName}
          </Typography>
          <Typography component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
          <Chip label={`${parsed.rowCount} rows`} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 500 }} />
          {parsed.colCount && (
            <Chip label={`${parsed.colCount} cols`} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 500 }} />
          )}
          {parsed.inputRefs && (
            <>
              <Typography component="span" sx={{ fontSize: 11, color: 'text.disabled' }}>·</Typography>
              <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
                from{' '}
                <Box
                  component="span"
                  sx={{ fontFamily: '"Fira Code", "Roboto Mono", monospace', fontSize: 11, fontWeight: 500 }}
                >
                  {parsed.inputRefs}
                </Box>
              </Typography>
            </>
          )}
        </>
      ) : (
        /* Fallback: render raw content if parsing failed */
        <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>
          {message.content}
        </Typography>
      )}
    </Box>
  );
};

// ── Main Component ───────────────────────────────────────────────────

/** Mapping complete CTA card — shown after mapping generation finishes. */
const MappingCompleteCTA: React.FC<{ message: WorkspaceMessage; onOpenEditor?: () => void }> = ({ message, onOpenEditor }) => {
  const theme = useTheme();
  const totalMappings = message.metadata?.total_mappings ?? 0;
  const avgConfidence = message.metadata?.avg_confidence ?? 0;

  return (
    <Box
      sx={{
        mx: 1,
        my: 1,
        p: 2,
        borderRadius: 2,
        border: 1,
        borderColor: alpha(theme.palette.success.main, 0.3),
        bgcolor: alpha(theme.palette.success.main, 0.04),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <CompleteIcon sx={{ fontSize: 20, color: 'success.main' }} />
        <Typography variant="body2" fontWeight={600}>
          Mapping Complete
        </Typography>
      </Stack>
      {totalMappings > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {totalMappings} fields mapped · {Math.round(avgConfidence * 100)}% avg confidence
        </Typography>
      )}
      {message.content && (
        <Typography variant="body2" sx={{ mb: 1.5, fontSize: 13 }}>
          {message.content}
        </Typography>
      )}
      <Stack direction="row" spacing={1}>
        {onOpenEditor && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditorIcon sx={{ fontSize: 14 }} />}
            onClick={onOpenEditor}
            sx={{ textTransform: 'none', fontSize: 12 }}
          >
            Open Mapping Editor
          </Button>
        )}
      </Stack>
    </Box>
  );
};

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onOptionClick, onSuggestionClick, isLatestAgentMessage, onDismissFailure, onOpenEditor, onFocusNode }) => {
  const variant = getMessageVariant(message);
  const timeLabel = formatTimestamp(message.created_at);
  const isUser = variant === 'user';

  let inner: React.ReactNode;
  switch (variant) {
    case 'system':
      inner = <SystemMessageBar text={message.content || ''} />;
      break;
    case 'correction':
      inner = <CorrectionMessage message={message} />;
      break;
    case 'warning':
      inner = <WarningMessage message={message} onOptionClick={onOptionClick} />;
      break;
    case 'mapping_complete':
      inner = <MappingCompleteCTA message={message} onOpenEditor={onOpenEditor} />;
      break;
    case 'tool_result':
      inner = <ToolResultBar message={message} onFocusNode={onFocusNode} />;
      break;
    default:
      inner = (
        <ChatBubble
          message={message}
          onDismissFailure={onDismissFailure}
          onSuggestionClick={onSuggestionClick}
          isLatestAgentMessage={isLatestAgentMessage}
        />
      );
      break;
  }

  return (
    <Box>
      {inner}
      {timeLabel && (
        <Tooltip title={timeLabel.full} placement={isUser ? 'left' : 'right'} arrow>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              fontSize: 9,
              color: 'text.disabled',
              mt: 0.25,
              textAlign: isUser ? 'right' : 'left',
              px: 0.5,
              cursor: 'default',
            }}
          >
            {timeLabel.short}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};

export default ChatMessage;
